import { sendAnalytics } from '@audio-network/analytics'
import {
	Button,
	Checkbox,
	Modal,
	ModalActions,
	ModalContent,
	ModalHeading,
	Paragraph,
	Tooltip,
} from '@audio-network/componentlibrary'
import {
	Delete,
	Download as DownloadIcon,
	Folder,
} from '@audio-network/componentlibrary/icons'
import clsx from 'clsx'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useId } from 'react'

import { DeleteTracksModal } from '@/components/pages/project/Modals'
import AddToProjectsModal from '@/components/shared/AddToProjectsModal/AddToProjectsModal'
import DownloadUnavailable from '@/components/shared/DownloadUnavailable/DownloadUnavailable'
import { DOWNLOAD_HISTORY, FAVOURITES, SEARCH } from '@/constants/trackSources'
import { useActiveProject } from '@/hooks/useActiveProject'
import useFavouritesStore from '@/stores/favouritesStore'
import sessionStore from '@/stores/sessionStore'
import { isTrialCustomer } from '@/utils/user/isTrialCustomer'

import { getTrackListHeadingText } from '../getTrackListHeadingText'
import styles from './ActionBar.module.css'
import { ActionBarProps } from './ActionBar.types'

const DownloadOptionsModal = dynamic(
	() => import('@/components/shared/DownloadOptionsModal'),
	{
		ssr: false,
	}
)

/**
 * @description
 * As a user, I want a sticky action bar with track count and bulk actions, so that I can download or manage selected tracks.
 * */
const ActionBar = ({
	download,
	className,
	isCatalogueAvailable = true,
	onClearSelection,
	onSelectAll,
	source,
	tracks,
	selectedTracks,
	...props
}: ActionBarProps): JSX.Element | null => {
	const { pathname, asPath } = useRouter()
	const routeId =
		(asPath ?? pathname ?? '')
			.split(/[?#]/)[0]
			.replace(/^\/+/, '')
			.replaceAll(/[^a-zA-Z0-9_-]+/g, '-') || 'route'
	// Route + useId so modal state remounts on project/album/playlist navigation,
	// while still staying unique when multiple action bars share a route.
	// React useId() includes ':' which is invalid in HTML id/commandfor values and
	// breaks our [commandfor="…"] attribute selector in DownloadOptionsModal.
	const instanceId = `${routeId}-${useId().replaceAll(':', '')}`
	const addToProjectModalId = `action-bar-add-to-project-modal-${instanceId}`
	const deleteTracksModalId = `action-bar-delete-tracks-modal-${instanceId}`
	const clearFavouritesModalId = `action-bar-clear-favourites-modal-${instanceId}`
	const clearFavouritesModalHeadingId = `clear-favourites-modal-heading-${instanceId}`
	const clearFavouritesModalDescriptionId = `clear-favourites-modal-description-${instanceId}`
	const downloadOptionsModalId = `action-bar-download-modal-${instanceId}`
	const { onTracksDelete } = useActiveProject()
	const clearAllFavourites = useFavouritesStore(
		(state) => state.clearAllFavourites
	)
	const removeFavourites = useFavouritesStore((state) => state.removeFavourites)
	const user = sessionStore((state) => state.session?.user)
	const canDownload = user?.organisation.canDownload
	const canUseDownloadOptions = !!user && !isTrialCustomer(user)
	const { t: appT } = useTranslation(['app'])
	const { t: downloadT } = useTranslation(['download'])
	const { t: favouritesT } = useTranslation(['favourites'])

	const areTracksSelected = selectedTracks.length > 0
	const isFavouritesPage = pathname === '/favourites'

	// Search and download history keep this hidden until a track is selected.
	const showActionBar =
		tracks.length > 0 &&
		((source !== SEARCH && source !== DOWNLOAD_HISTORY) || areTracksSelected)
	const headingCount = areTracksSelected ? selectedTracks.length : tracks.length
	const headingMode = areTracksSelected ? 'selected' : 'total'
	const trackCount = getTrackListHeadingText(
		headingCount,
		source,
		headingMode,
		appT
	)

	const getDownloadTracks = (): Track[] => {
		if (areTracksSelected) {
			return selectedTracks
		}

		if (download?.canDownloadAll) {
			return tracks
		}

		return []
	}

	const downloadTracks = getDownloadTracks()
	const addToProjectTracks = areTracksSelected ? selectedTracks : tracks

	const showDeleteButton = onTracksDelete && areTracksSelected
	const showDownload =
		source !== SEARCH && (!!canDownload || !isCatalogueAvailable)
	const showDownloadOptionsModal =
		showDownload && isCatalogueAvailable && canUseDownloadOptions
	const showClearFavouritesButton = isFavouritesPage
	const downloadButtonLabel = areTracksSelected
		? downloadT('download-selected')
		: downloadT('download-all')
	const addToProjectLabel = appT('action-tray.add-to-project-label')
	const deleteLabel = appT('action-tray.delete-label')
	const clearFavouritesLabel = favouritesT('clear-favourites.heading')

	const handleDownloadOptionsOpen = (): void => {
		sendAnalytics({
			noun: 'Download Options',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Location: 'Action Bar',
			},
		})
	}

	const handleAddToProjectOpen = (): void => {
		sendAnalytics({
			noun: 'Add to Project',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				TrackSource: source,
			},
		})
	}

	const handleDeleteModalOpen = (): void => {
		sendAnalytics({
			noun: 'Delete Tracks',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				TrackSource: source,
			},
		})
	}

	const handleClearFavouritesModalOpen = (): void => {
		sendAnalytics({
			noun: 'Clear Favourites',
			pastTenseVerb: 'Clicked',
		})
	}

	const handleClearFavourites = (): void => {
		if (areTracksSelected) {
			removeFavourites(selectedTracks, FAVOURITES)
			onClearSelection()
		} else {
			clearAllFavourites()
		}

		sendAnalytics({
			noun: 'Clear Favourites',
			pastTenseVerb: 'Completed',
		})
	}

	if (!showActionBar) {
		return null
	}

	return (
		<>
			<div
				className={clsx(
					styles.actionBar,
					areTracksSelected && styles.hasSelected,
					className
				)}
				data-testid="track-list-action-panel"
				{...props}
			>
				<Checkbox
					className={styles.selectAllCheckbox}
					type="checkbox"
					aria-label={areTracksSelected ? appT('deselect-all') : undefined}
					id="select-all"
					onChange={onSelectAll}
					checked={areTracksSelected}
					label={trackCount}
					name="track-list-select-all-checkbox"
				/>
				{showDownload ? (
					isCatalogueAvailable ? (
						canUseDownloadOptions ? (
							<Button
								className={styles.downloadButton}
								command="show-modal"
								commandFor={downloadOptionsModalId}
								onClick={handleDownloadOptionsOpen}
								size="S"
								startIcon={<DownloadIcon />}
							>
								{downloadButtonLabel}
							</Button>
						) : null
					) : (
						<DownloadUnavailable className={styles.downloadUnavailable} />
					)
				) : null}
				<span className={styles.iconButton}>
					<Tooltip content={addToProjectLabel}>
						<button
							onClick={handleAddToProjectOpen}
							type="button"
							{...{
								command: 'show-modal',
								commandfor: addToProjectModalId,
							}}
						>
							<Folder />
						</button>
					</Tooltip>
				</span>
				<Button
					className={styles.button}
					command="show-modal"
					commandFor={addToProjectModalId}
					onClick={handleAddToProjectOpen}
					size="S"
					startIcon={<Folder />}
					variant="secondary"
				>
					{addToProjectLabel}
				</Button>

				{showDeleteButton && (
					<>
						<span className={styles.iconButton}>
							<Tooltip content={deleteLabel}>
								<button
									onClick={handleDeleteModalOpen}
									type="button"
									{...{
										command: 'show-modal',
										commandfor: deleteTracksModalId,
									}}
								>
									<Delete />
								</button>
							</Tooltip>
						</span>
						<Button
							className={styles.button}
							command="show-modal"
							commandFor={deleteTracksModalId}
							onClick={handleDeleteModalOpen}
							size="S"
							startIcon={<Delete />}
							variant="secondary"
						>
							{deleteLabel}
						</Button>
					</>
				)}

				{showClearFavouritesButton && (
					<>
						<span className={styles.iconButton}>
							<Tooltip content={clearFavouritesLabel}>
								<button
									onClick={handleClearFavouritesModalOpen}
									type="button"
									{...{
										command: 'show-modal',
										commandfor: clearFavouritesModalId,
									}}
								>
									<Delete />
								</button>
							</Tooltip>
						</span>
						<Button
							className={styles.button}
							command="show-modal"
							commandFor={clearFavouritesModalId}
							onClick={handleClearFavouritesModalOpen}
							size="S"
							startIcon={<Delete />}
							variant="secondary"
						>
							{clearFavouritesLabel}
						</Button>
					</>
				)}
			</div>
			{/* Keep modals outside of the action bar because CSS animation-timeline causes problems for modal animation */}
			{showDownloadOptionsModal && (
				<DownloadOptionsModal
					key={routeId}
					isSelection={areTracksSelected}
					modalId={downloadOptionsModalId}
					trackSource={source}
					tracks={downloadTracks}
					zipFileName={download?.name}
				/>
			)}
			<AddToProjectsModal
				id={addToProjectModalId}
				onSuccess={onClearSelection}
				source={source}
				tracks={addToProjectTracks}
			/>
			{showDeleteButton && (
				<DeleteTracksModal
					id={deleteTracksModalId}
					onDeleteSuccess={onClearSelection}
					source={source}
					tracks={selectedTracks}
				/>
			)}
			{showClearFavouritesButton && (
				<Modal
					aria-describedby={clearFavouritesModalDescriptionId}
					aria-labelledby={clearFavouritesModalHeadingId}
					id={clearFavouritesModalId}
				>
					<ModalHeading id={clearFavouritesModalHeadingId}>
						{favouritesT('clear-favourites.heading')}
					</ModalHeading>
					<ModalContent>
						<Paragraph id={clearFavouritesModalDescriptionId}>
							{favouritesT('clear-favourites.confirmation-message')}
						</Paragraph>
					</ModalContent>
					<ModalActions>
						<Button
							command="close"
							commandFor={clearFavouritesModalId}
							data-testid="accept-clear-favourites"
							onClick={handleClearFavourites}
							type="button"
						>
							{favouritesT('clear-favourites.yes')}
						</Button>
						<Button
							command="close"
							commandFor={clearFavouritesModalId}
							type="button"
							variant="secondary"
						>
							{favouritesT('clear-favourites.no')}
						</Button>
					</ModalActions>
				</Modal>
			)}
		</>
	)
}

export default ActionBar
